import React, { useCallback, useState, forwardRef } from 'react';
import { FloatingFocusManager, offset, shift, useClick, useDismiss, useFloating, useInteractions, useMergeRefs, useRole, } from '@floating-ui/react';
import { PrimaryButton, SecondaryButton, ToolkitFeatureFlag } from '@cko/primitives';
import { trackEvent } from '../../utils';
import { useIsBlockedHelper } from '../../hooks';
import { BlockedModalButton } from './BlockedModalButton';
import { StyledButtonContainer, StyledBlockedModal, StyledSwitchText, } from './BlockedLinkWrapper.styles';
const modalOffset = {
    primary: {
        crossAxisOffset: -1,
        mainAxisOffset: 8,
    },
    secondary: {
        crossAxisOffset: -1,
        mainAxisOffset: 6,
    },
};
const BLOCKED_LINK_MODAL_TEST_IDS = {
    modalCancelButton: 'test-cancel-view-button',
    modalChangeViewButton: 'test-change-view-button',
    modalWrapper: 'test-blocked-modal-wrapper',
};
export const BlockedLinkWrapper = forwardRef(({ children, linkType, to, requiredAccountLevels }, ref) => {
    const selectedOffset = modalOffset[linkType];
    const { isBlocked, selectedAccountLevel, requiredAccountLevelName, switchToRequiredAccountLevel, switchViewText, } = useIsBlockedHelper(requiredAccountLevels);
    const [showBlockedModal, setShowBlockedModal] = useState(false);
    const { floatingStyles, refs, context } = useFloating({
        open: showBlockedModal,
        onOpenChange(isOpen) {
            setShowBlockedModal(isOpen);
            if (isOpen) {
                trackEvent('Click - blocked segment-agnostic view - navigation link', {
                    linkType,
                    to,
                    requiredAccountLevel: requiredAccountLevels?.[0],
                    selectedAccountLevel,
                });
            }
        },
        middleware: [
            offset({
                mainAxis: selectedOffset.mainAxisOffset,
                crossAxis: selectedOffset.crossAxisOffset,
            }),
            shift(),
        ],
        placement: 'bottom-start',
    });
    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);
    const onSwitchAccountLevel = useCallback(() => {
        setShowBlockedModal(false);
        switchToRequiredAccountLevel?.(to);
    }, [setShowBlockedModal, switchToRequiredAccountLevel, to]);
    const analyticsMetadata = {
        path: to,
        requiredAccountLevel: (requiredAccountLevels && requiredAccountLevels[0]) || 'client',
        selectedAccountLevel: selectedAccountLevel,
    };
    const mergedRef = useMergeRefs([ref, refs.setReference]);
    const handleCancelClick = () => {
        if (analyticsMetadata) {
            trackEvent('Click - blocked segment-agnostic view - modal - cancel button', analyticsMetadata);
        }
        setShowBlockedModal(false);
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(BlockedModalButton, { isBlocked: isBlocked, ref: mergedRef, ...getReferenceProps() }, children({ isBlocked })),
        showBlockedModal && (React.createElement(FloatingFocusManager, { context: context, modal: false },
            React.createElement(StyledBlockedModal, { "data-testid": BLOCKED_LINK_MODAL_TEST_IDS.modalWrapper, ref: refs.setFloating, style: floatingStyles, ...getFloatingProps() },
                React.createElement(StyledSwitchText, null, switchViewText),
                React.createElement(ToolkitFeatureFlag, { flag: "v10" },
                    React.createElement(StyledButtonContainer, null,
                        requiredAccountLevelName && (React.createElement(PrimaryButton, { "data-testid": BLOCKED_LINK_MODAL_TEST_IDS.modalChangeViewButton, onClick: onSwitchAccountLevel }, "Switch view")),
                        React.createElement(SecondaryButton, { "data-testid": BLOCKED_LINK_MODAL_TEST_IDS.modalCancelButton, onClick: handleCancelClick }, "Cancel"))))))));
});
